import api from '../utils/api';
import authService from './authService';

const restaurantService = {
  // Suggest available (unregistered) email domains for a restaurant name
  suggestDomains: async (name) => {
    try {
      const response = await api.get('/restaurants/suggest-domains', { params: { name } });
      return response.data?.suggestions || [];
    } catch (error) {
      return [];
    }
  },

  getAllRestaurants: async () => {
    try {
      const currentUser = authService.getUserFromStorage();
      
      const response = await api.get('/restaurants');
      
      let processedData = [];
      if (Array.isArray(response.data)) {
        processedData = response.data.map(restaurant => {
          return {
            ...restaurant,
            id: restaurant._id || restaurant.id,
            _id: restaurant.id || restaurant._id
          };
        });
        
        if (currentUser && currentUser.role !== 'owner' && !currentUser.permissions?.manageRestaurants) {
          processedData = processedData.filter(restaurant => 
            String(restaurant.id) === String(currentUser.restaurantId)
          );
        }
      }
      
      return processedData;
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      return [];
    }
  },

  getRestaurantById: async (id) => {
    try {
      const restaurantId = typeof id === 'object' ? (id._id || id.toString()) : String(id).trim();
      
      const response = await api.get(`/restaurants/${restaurantId}`);
      
      if (response.data && response.data._id && !response.data.id) {
        response.data.id = response.data._id;
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error fetching restaurant' };
    }
  },

  createRestaurant: async (restaurantData, options = {}) => {
    try {
      if (restaurantData.image || restaurantData.logo) {
        const formData = new FormData();
        
        Object.keys(restaurantData).forEach(key => {
          if ((key === 'image' || key === 'logo') && restaurantData[key]) {
            formData.append(key, restaurantData[key]);
          } else {
            formData.append(key, restaurantData[key]);
          }
        });
        
        const response = await api.post('/restaurants', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: options.onUploadProgress
        });
        
        return response.data;
      } 
      else {
        const response = await api.post('/restaurants', restaurantData);
        return response.data;
      }
    } catch (error) {
      const data = error.response?.data;
      let errorMessage = data?.message || 'Server error when creating restaurant';
      // Surface specific field errors (e.g. "Phone number must be 10-15 digits")
      // instead of the generic "Validation failed".
      if (data?.errors && typeof data.errors === 'object') {
        const fieldMsgs = Object.values(data.errors).filter(Boolean);
        if (fieldMsgs.length) errorMessage = fieldMsgs.join('. ');
      }
      const err = new Error(errorMessage);
      err.details = data;
      throw err;
    }
  },

  updateRestaurant: async (id, restaurantData, options = {}) => {
    try {
      if (restaurantData.image || restaurantData.logo) {
        const formData = new FormData();
        
        Object.keys(restaurantData).forEach(key => {
          if ((key === 'image' || key === 'logo') && restaurantData[key]) {
            formData.append(key, restaurantData[key]);
          } else {
            formData.append(key, restaurantData[key]);
          }
        });
        
        const response = await api.put(`/restaurants/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: options.onUploadProgress
        });
        
        return response.data;
      } 
      else {
        const response = await api.put(`/restaurants/${id}`, restaurantData);
        return response.data;
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Server error when updating restaurant';
      const err = new Error(errorMessage);
      err.details = error.response?.data;
      throw err;
    }
  },



  deleteRestaurant: async (id) => {
    try {
      const response = await api.delete(`/restaurants/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error deleting restaurant' };
    }
  },

  getRestaurantAnalytics: async (restaurantId, timeRange = '30d') => {
    try {
      const response = await api.get(`/restaurants/${restaurantId}/analytics?timeRange=${timeRange}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching restaurant analytics:', error);
      return null;
    }
  },

  // Get restaurants associated with the current user
  getUserRestaurants: async () => {
    try {
      const currentUser = authService.getUserFromStorage();
      
      if (!currentUser) {
        return [];
      }
      
      const response = await api.get('/restaurants/user/me');
      
      let processedData = [];
      if (Array.isArray(response.data)) {
        processedData = response.data.map(restaurant => {
          return {
            ...restaurant,
            id: restaurant._id || restaurant.id,
            _id: restaurant.id || restaurant._id
          };
        });
      }
      
      return processedData;
    } catch (error) {
      console.error('Error fetching user restaurants:', error);
      return [];
    }
  }
};

export default restaurantService;